import { App, Button, Form, Select, Space, Table, Typography } from 'antd';
import { Link2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crestHttp } from '../../api/client.js';
import { FancyModal } from '../../components/ui/index.js';
import {
  AccessScopeSelector,
  type ScopePolicy,
  defaultAccessScopeConfig,
  hasInvalidSelectedScope,
} from './AccessScopeSelector.js';
import type { PositionRow, RoleCatalogRow, UserOption } from './access-center-types.js';

interface BindingRow {
  binding_id: string;
  status: string;
  scope_policy: string;
  role?: RoleCatalogRow;
}

interface PositionAssignmentRow {
  assignment_id: string;
  user_id: string;
  is_primary: boolean;
  status: string;
  user?: { fullname: string; domainname?: string | null } | null;
}

interface Props {
  position: PositionRow | null;
  roles: RoleCatalogRow[];
  users: UserOption[];
  onClose: () => void;
}

export function AccessPositionConfigurationModal({ position, roles, users, onClose }: Props) {
  const { t } = useTranslation('accessCenter');
  const { message } = App.useApp();
  const [bindings, setBindings] = useState<BindingRow[]>([]);
  const [assignments, setAssignments] = useState<PositionAssignmentRow[]>([]);
  const [roleId, setRoleId] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [scopePolicy, setScopePolicy] = useState<ScopePolicy>('role_default');
  const [scopeConfig, setScopeConfig] = useState(defaultAccessScopeConfig);
  const role = useMemo(
    () => roles.find((item) => item.role_catalog_id === roleId),
    [roleId, roles],
  );

  const load = useCallback(async () => {
    if (!position) return;
    const [bindingRows, detail] = await Promise.all([
      crestHttp.get<BindingRow[]>(`/admin/access/positions/${position.position_id}/role-bindings`),
      crestHttp.get<PositionRow & { assignments: PositionAssignmentRow[] }>(
        `/admin/access/positions/${position.position_id}`,
      ),
    ]);
    setBindings(bindingRows.data ?? []);
    setAssignments(detail.data.assignments ?? []);
  }, [position]);

  useEffect(() => {
    setRoleId(undefined);
    setUserId(undefined);
    resetScope();
    void load();
  }, [load]);

  const addBinding = async () => {
    if (!position || !roleId) return;
    if (scopePolicy === 'explicit' && hasInvalidSelectedScope(scopeConfig)) {
      message.error(t('scopeRequired'));
      return;
    }
    await crestHttp.post(`/admin/access/positions/${position.position_id}/role-bindings`, {
      roleCatalogId: roleId,
      scopePolicy: role?.domain === 'crm' ? scopePolicy : 'role_default',
      scopeConfig: role?.domain === 'crm' && scopePolicy === 'explicit' ? scopeConfig : {},
    });
    message.success(t('bindingCreated'));
    setRoleId(undefined);
    resetScope();
    await load();
  };

  const addAssignment = async () => {
    if (!position || !userId) return;
    await crestHttp.post(`/admin/access/positions/${position.position_id}/assignments`, {
      userId,
      isPrimary: false,
    });
    message.success(t('assignmentCreated'));
    setUserId(undefined);
    await load();
  };

  const endAssignment = async (assignmentId: string) => {
    await crestHttp.post(`/admin/access/positions/assignments/${assignmentId}/end`);
    message.success(t('assignmentEnded'));
    await load();
  };

  const resetScope = () => {
    setScopePolicy('role_default');
    setScopeConfig(defaultAccessScopeConfig());
  };

  return (
    <FancyModal
      title={t('configureRolesFor', { name: position?.name_zh })}
      subtitle={t('bindingSubtitle')}
      icon={<Link2 size={18} />}
      open={Boolean(position)}
      onCancel={onClose}
      footer={null}
      width={720}
    >
      <Form layout="vertical">
        <Form.Item label={t('roleName')} required>
          <Select
            showSearch
            optionFilterProp="label"
            value={roleId}
            onChange={(value) => {
              setRoleId(value);
              resetScope();
            }}
            placeholder={t('selectRole')}
            options={roles.map((item) => ({
              value: item.role_catalog_id,
              label: `[${item.domain.toUpperCase()}] ${item.name}`,
            }))}
          />
        </Form.Item>
        <AccessScopeSelector
          role={role}
          mode="position"
          scopePolicy={scopePolicy}
          scopeConfig={scopeConfig}
          onPolicyChange={(value) => {
            setScopePolicy(value);
            setScopeConfig(defaultAccessScopeConfig());
          }}
          onScopeConfigChange={setScopeConfig}
        />
        <Button type="primary" disabled={!roleId} onClick={addBinding}>
          {t('addRoleBinding')}
        </Button>
      </Form>
      <Table
        rowKey="binding_id"
        size="small"
        pagination={false}
        dataSource={bindings}
        style={{ marginTop: 16 }}
        columns={[
          {
            title: t('roleName'),
            render: (_, row) => `[${row.role?.domain.toUpperCase()}] ${row.role?.name}`,
          },
          {
            title: t('scopePolicy'),
            dataIndex: 'scope_policy',
            width: 180,
            render: (value) => t(`scope_${value}`),
          },
          { title: t('common:status'), dataIndex: 'status', width: 100 },
        ]}
      />
      <Typography.Title level={5} style={{ marginTop: 24 }}>
        {t('positionMembers')}
      </Typography.Title>
      <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
        <Select
          showSearch
          optionFilterProp="label"
          value={userId}
          onChange={setUserId}
          placeholder={t('selectUser')}
          options={users.map((user) => ({
            value: user.systemuserid,
            label: `${user.fullname} · ${user.domainname ?? ''}`,
          }))}
          style={{ flex: 1 }}
        />
        <Button type="primary" disabled={!userId} onClick={addAssignment}>
          {t('assignPosition')}
        </Button>
      </Space.Compact>
      <Table
        rowKey="assignment_id"
        size="small"
        pagination={false}
        dataSource={assignments}
        columns={[
          {
            title: t('user'),
            render: (_, row) =>
              row.user?.fullname ??
              users.find((user) => user.systemuserid === row.user_id)?.fullname ??
              row.user_id,
          },
          {
            title: t('positionType'),
            render: (_, row) => (row.is_primary ? t('primaryPosition') : t('concurrentPosition')),
          },
          { title: t('common:status'), dataIndex: 'status', width: 100 },
          {
            title: t('actions'),
            width: 90,
            render: (_, row) =>
              !['ended', 'cancelled'].includes(row.status) && (
                <Button type="link" danger onClick={() => endAssignment(row.assignment_id)}>
                  {t('endAssignment')}
                </Button>
              ),
          },
        ]}
      />
    </FancyModal>
  );
}
